import { useState } from 'react';
import ConditionGroupEditor from './ConditionGroupEditor';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { ArrowLeft, Save, Zap, BookOpen, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { eventTypes, generateRuleSummary, ruleSnapshotToJson } from '../../../data/rulesData';
import type { Rule, RuleStatus, EventType, ConditionGroup } from '../../../types/rule.types';

interface RuleBuilderProps {
  rule: Rule;
  onBack: () => void;
  onSave: (updated: Rule) => void;
  isSaving?: boolean;
}

export default function RuleBuilder({ rule, onBack, onSave, isSaving }: RuleBuilderProps) {
  const [draft, setDraft] = useState<Rule>({ ...rule, conditionGroup: structuredClone(rule.conditionGroup) });

  const summary = generateRuleSummary(draft);

  const toggleEvent = (eventId: EventType) => {
    const current = draft.eventTypes;
    const updated = current.includes(eventId)
      ? current.filter(e => e !== eventId)
      : [...current, eventId];
    if (updated.length === 0) return;
    setDraft({ ...draft, eventTypes: updated });
  };

  const handleConditionGroupChange = (updated: ConditionGroup) => {
    setDraft({ ...draft, conditionGroup: updated });
  };

  const handleSave = () => {
    onSave(draft);
  };

  const conditionCount = countConditions(draft.conditionGroup);

  return (
    <div className="h-full flex flex-col bg-studio-shell">
      <div className="flex items-center justify-between h-11 px-4 border-b border-border bg-studio-surface shrink-0">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onBack}>
            <ArrowLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-sm font-semibold text-foreground">Rule Builder</span>
          <span className="text-xs text-muted-foreground font-mono">· {draft.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onBack}>Cancel</Button>
          <Button size="sm" className="h-7 text-xs" onClick={handleSave} disabled={isSaving} data-tour="rule-save">
            <Save className="h-3 w-3 mr-1" />
            {isSaving ? 'Saving...' : 'Save Rule'}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto studio-scrollbar">
        <div className="max-w-3xl mx-auto p-6 space-y-6">
          <div className="bg-studio-surface rounded-lg border border-border p-5 space-y-4">
            <div className="space-y-2" data-tour="rule-name">
              <Label className="text-xs text-muted-foreground">Rule Name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-8 text-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="text-sm min-h-[60px] resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-tour="rule-priority">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v as RuleStatus })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft" className="text-xs">Draft</SelectItem>
                    <SelectItem value="active" className="text-xs">Active</SelectItem>
                    <SelectItem value="inactive" className="text-xs">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Priority</Label>
                <Input type="number" min={1} value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) || 1 })} className="h-8 text-xs" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={draft.stopAfterMatch} onCheckedChange={(checked) => setDraft({ ...draft, stopAfterMatch: checked })} />
              <div>
                <Label className="text-xs">Stop after match</Label>
                <p className="text-[11px] text-muted-foreground">If this rule matches, skip lower-priority rules.</p>
              </div>
            </div>
          </div>

          <div className="bg-studio-surface rounded-lg border border-border p-5 space-y-3" data-tour="rule-events">
            <div className="flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-email" />
              <h3 className="text-sm font-semibold text-foreground">Trigger Events</h3>
            </div>
            <p className="text-xs text-muted-foreground">Select which events will trigger this rule evaluation.</p>
            <div className="flex flex-wrap gap-2">
              {eventTypes.map(et => {
                const selected = draft.eventTypes.includes(et.id);
                return (
                  <button
                    key={et.id}
                    onClick={() => toggleEvent(et.id)}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors ${
                      selected
                        ? 'bg-email/10 border-email/30 text-email font-medium'
                        : 'bg-background border-border text-muted-foreground hover:border-foreground/20'
                    }`}
                  >
                    <Zap className="h-2.5 w-2.5" />
                    {et.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-studio-surface rounded-lg border border-border p-5 space-y-3" data-tour="rule-conditions">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Conditions</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {conditionCount} condition{conditionCount !== 1 ? 's' : ''} · Build nested AND/OR logic
              </p>
            </div>
            <ConditionGroupEditor group={draft.conditionGroup} onChange={handleConditionGroupChange} />
          </div>

          <div className="bg-studio-surface rounded-lg border border-border p-5 space-y-3">
            <div className="flex items-center gap-2">
              <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-foreground">Rule Summary</h3>
            </div>
            <div className="rounded-md bg-muted/50 border border-border p-3">
              <p className="text-xs leading-relaxed text-foreground">{summary}</p>
            </div>
            {draft.eventTypes.length === 0 && (
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span className="text-xs">At least one trigger event is required.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function countConditions(group: ConditionGroup): number {
  return group.conditions.length + group.groups.reduce((sum, g) => sum + countConditions(g), 0);
}
